class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // HE THEME: Reduced debounce for better mobile responsiveness
    // Desktop: 500ms (prevents multiple requests when clicking multiple filters)
    // Mobile: 300ms (feels more responsive on touch devices)
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const debounceTime = isMobile ? 100 : 500;
    
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, debounceTime);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const gridContent = document.getElementById('ProductGridContainer')?.querySelector('.collection, .ms-products-grid, [data-id]');
    if (gridContent) gridContent.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceContainer = parsed.getElementById('ProductGridContainer');
    const targetContainer = document.getElementById('ProductGridContainer');
    if (!sourceContainer || !targetContainer) return;

    targetContainer.innerHTML = sourceContainer.innerHTML;
    targetContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsed.getElementById('ProductCount') || parsed.querySelector('.ms-results-count') || parsed.querySelector('.ms-no-results');
    if (!countEl) return;

    const count = countEl.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    const containerMsResults = document.querySelector('.ms-results-count');

    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    if (containerMsResults) {
      containerMsResults.innerHTML = count;
    }
    const containerMsNoResults = document.querySelector('.ms-no-results');
    if (containerMsNoResults) {
      containerMsNoResults.innerHTML = count;
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    console.log('🔍 DEBUG renderFilters:');
    console.log('  - Filters found in FETCH:', facetDetailsElementsFromFetch.length);
    console.log('  - Filters found in DOM:', facetDetailsElementsFromDom.length);
    
    // Log which forms have filters
    const formsInFetch = {
      desktop: parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter').length,
      mobile: parsedHTML.querySelectorAll('#FacetFiltersFormMobile .js-filter').length,
      pills: parsedHTML.querySelectorAll('#FacetFiltersPillsForm .js-filter').length
    };
    console.log('  - Forms in FETCH:', formsInFetch);
    
    const formsInDOM = {
      desktop: document.querySelectorAll('#FacetFiltersForm .js-filter').length,
      mobile: document.querySelectorAll('#FacetFiltersFormMobile .js-filter').length,
      pills: document.querySelectorAll('#FacetFiltersPillsForm .js-filter').length
    };
    console.log('  - Forms in DOM:', formsInDOM);

    // HE THEME MODIFICATION: Skip filter updates if no filters found in fetch
    // This prevents sidebar filters from being wiped when filters are in separate section
    if (facetDetailsElementsFromFetch.length === 0) {
      console.log('⚠️ No filters found in fetched HTML - skipping filter update to preserve sidebar');
      
      // Still render active facets and additional elements
      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);
      
      // HE THEME: Sync checkboxes and Remove all button (early exit case)
      // Small delay ensures URL is fully updated before sync
      setTimeout(() => {
        FacetFiltersForm.syncCheckboxesWithURL();
      }, 50);
      
      return;
    }
    
    console.log('✅ Continuing with filter update (filters found in fetch)');

    console.log('✅ Continuing with filter update (filters found in fetch)');

    // HE THEME MODIFICATION: Get section IDs from fetched filters to only remove filters from those sections
    // This prevents removing filters from other sections (like he-sidebar)
    const sectionIdsInFetch = new Set();
    Array.from(facetDetailsElementsFromFetch).forEach((element) => {
      // Extract section ID from element ID (e.g., "Details-filter.xxx-SECTION_ID")
      const match = element.id.match(/-(template--[^-]+__[^-]+|he-sidebar)$/);
      if (match) {
        sectionIdsInFetch.add(match[1]);
      }
    });
    console.log('  - Section IDs in fetch:', Array.from(sectionIdsInFetch));

    // Remove facets that are no longer returned from the server
    // BUT ONLY if they belong to the same section that's being fetched
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      // Check if this element belongs to one of the sections being fetched
      const belongsToFetchedSection = Array.from(sectionIdsInFetch).some(sectionId => 
        currentElement.id.endsWith(`-${sectionId}`)
      );
      
      // Only remove if it belongs to a fetched section AND is not in the fetch results
      if (belongsToFetchedSection && !Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        console.log('🗑️ Removing filter element:', currentElement.id);
        currentElement.remove();
      } else if (!belongsToFetchedSection) {
        console.log('✋ Preserving filter from other section:', currentElement.id);
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);
    
    console.log('  - Facets to render:', facetsToRender.length);
    console.log('  - Counts to render:', countsToRender ? countsToRender.id : 'none');

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      console.log(`  - Processing filter ${index + 1}/${facetsToRender.length}:`, elementToRender.id);
      console.log('    Current element exists in DOM:', !!currentElement);
      
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        console.log('    ✏️ Updating innerHTML for:', elementToRender.id);
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    // HE THEME: ALWAYS sync checkboxes with URL params (after normal flow too)
    // This ensures checkboxes are correct when checking/unchecking filters
    // Small delay ensures URL is fully updated before sync
    setTimeout(() => {
      FacetFiltersForm.syncCheckboxesWithURL();
    }, 50);
  }

  // HE THEME: Sync checkboxes with URL params and toggle "Remove all" button
  // Called after every filter update (both normal flow and early exit)
  static syncCheckboxesWithURL() {
    const currentParams = new URLSearchParams(window.location.search);
    const activeFilters = new Set();
    
    // Collect all active filter values from URL (ignore empty values)
    for (const [key, value] of currentParams.entries()) {
      if (key.startsWith('filter.')) {
        if (value && value.trim() !== '') {
          activeFilters.add(`${key}=${value}`);
        }
      }
    }
    
    // Update all checkboxes in the sidebar
    const checkboxes = document.querySelectorAll('#FacetFiltersForm input[type="checkbox"]');
    
    checkboxes.forEach((checkbox) => {
      const filterKey = checkbox.name;
      const filterValue = checkbox.value;
      const filterParam = `${filterKey}=${filterValue}`;
      const shouldBeChecked = activeFilters.has(filterParam);
      const currentlyChecked = checkbox.checked;

      if (currentlyChecked !== shouldBeChecked) {
        checkbox.checked = shouldBeChecked;
      }

      const label = checkbox.closest('.facet-checkbox') || checkbox.closest('label');
      if (label) {
        label.classList.toggle('active', shouldBeChecked);
      }
    });
    
    // Hide/show "Remove all" button based on active filters
    const removeAllButtons = document.querySelectorAll('.active-facets__button-wrapper');
    
    removeAllButtons.forEach((button, index) => {
      if (activeFilters.size > 0) {
        button.classList.add('has-active-filters');  // Show when there are active filters
      } else {
        button.classList.remove('has-active-filters');  // Hide when no active filters
      }
    });
    
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const sections = [];

    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return [];

    // Soportar product-grid (main-search, collection) y search-grid (he-main-search)
    const gridEl = document.getElementById('product-grid') || document.getElementById('search-grid');
    const gridSectionId = gridEl?.dataset?.id;
    if (gridSectionId) sections.push({ section: gridSectionId });

    // Incluir he-sidebar para actualizar active-facets y filtros
    const sidebarFilters = document.querySelector('.sidebar-filters[data-section-id]');
    const sidebarSectionId = sidebarFilters?.dataset?.sectionId;
    if (sidebarSectionId && !sections.some((s) => s.section === sidebarSectionId)) {
      sections.push({ section: sidebarSectionId });
    }

    return sections;
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// HE THEME: Sync checkboxes and "Remove all" button on initial page load
// This ensures correct state when page is refreshed or loaded with/without filters
document.addEventListener('DOMContentLoaded', () => {
  // Small delay to ensure DOM is fully rendered
  setTimeout(() => {
    FacetFiltersForm.syncCheckboxesWithURL();
  }, 100);
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
